"use client";

import React, { useState, useEffect } from "react";
import { TIER_RATE_BOUNDS } from "@repo/booking";
import { Button } from "../common/button";

export interface MatchingQuizProps {
  onComplete?: (answers: QuizAnswers) => void | Promise<void>;
  isSubmitting?: boolean;
  hideInsurance?: boolean; // Hide insurance step for partnership users
  hideRate?: boolean; // Hide rate step for partnership users
  showProvince?: boolean; // Add a province step (client app only — needed for BES top-up)
  partnershipName?: string; // Name of the partnership
  sessionsCovered?: number | null; // Number of sessions covered
  availableTherapistTypes?: string[]; // Which therapist types are available
  partnershipId?: string; // Partnership ID for tracking
}

interface QuizAnswers {
  ageVerification: 'yes' | 'no';
  sessionType: 'individual' | 'couples';
  needs: string[];
  insurance: string[];
  therapistType: 'resident' | 'associate';
  rate: number;
  timezone: string;
  availability: string[];
  province?: string;
}

export function MatchingQuiz({
  onComplete,
  isSubmitting = false,
  hideInsurance = false,
  hideRate = false,
  showProvince = false,
  partnershipName,
  sessionsCovered,
  availableTherapistTypes,
  partnershipId
}: MatchingQuizProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showTherapistInfo, setShowTherapistInfo] = useState(false);
  const [showResidentDisclosure, setShowResidentDisclosure] = useState(false);
  const [answers, setAnswers] = useState<Partial<QuizAnswers>>({
    needs: [],
    availability: [],
    insurance: [],
  });
  const [showRateSlider, setShowRateSlider] = useState(false);

  // Build steps dynamically based on partnership mode
  const allSteps = [
    {
      id: 'age-verification',
      question: "Before we begin",
      subtitle: partnershipName
        ? `We need to verify eligibility for ${partnershipName}'s therapy program. You'll create a free account at the end to see your matches.`
        : "We need to verify eligibility for our services. You'll create a free account at the end to see your matches.",
      type: 'single-select',
      options: [
        {
          id: 'yes',
          label: 'Yes, I am 17 or older',
          description: 'Or I am seeking support for someone who is 17 or older'
        },
        {
          id: 'no',
          label: 'No, I am under 17',
          description: 'Our services are for those 17 or older'
        }
      ]
    },
    {
      id: 'session-type',
      question: "What type of therapy are you looking for?",
      subtitle: "We offer both individual and couples therapy",
      type: 'single-select',
      options: [
        { 
          id: 'individual', 
          label: 'Individual',
          description: 'Personal therapy sessions'
        },
        { 
          id: 'couples', 
          label: 'Couples',
          description: 'Relationship therapy'
        }
      ]
    },
    {
      id: 'needs',
      question: "What brings you here?",
      subtitle: "Choose what feels most relevant right now (optional)",
      type: 'multi-select',
      options: [
        { id: 'anxiety', label: 'Anxiety or worry' },
        { id: 'depression', label: 'Depression or low mood' },
        { id: 'relationships', label: 'Relationships' },
        { id: 'stress', label: 'Stress or overwhelm' },
        { id: 'life-changes', label: 'Life transitions' },
        { id: 'self-growth', label: 'Personal growth' },
        { id: 'identity', label: 'Identity exploration' },
        { id: 'grief', label: 'Grief or loss' },
        { id: 'trauma', label: 'Past experiences' },
        { id: 'purpose', label: 'Finding purpose' },
        { id: 'not-sure', label: "I'm not sure yet" },
        { id: 'other', label: "Other" }
      ]
    },
    {
      id: 'insurance',
      question: "What insurance coverage do you have?",
      subtitle: "Select all that apply",
      type: 'multi-select',
      options: [
        { 
          id: 'rp', 
          label: 'Registered Psychotherapist (RP)',
          description: 'Covered by most extended health benefits'
        },
        { 
          id: 'rsw', 
          label: 'Registered Social Worker (RSW)',
          description: 'Note: Only available with Associate therapists'
        },
        { 
          id: 'none', 
          label: 'No insurance coverage'
        },
        { 
          id: 'other', 
          label: "I'm not sure / Other"
        }
      ]
    },
    {
      id: 'therapist-type',
      question: "Choose your therapist type",
      subtitle: partnershipName && sessionsCovered
        ? `${partnershipName} covers ${sessionsCovered} sessions with your chosen therapist type`
        : "Both options provide quality care",
      type: 'single-select',
      options: (() => {
        // For partnerships, only show covered options
        if (availableTherapistTypes && availableTherapistTypes.length > 0) {
          const options = [];
          if (availableTherapistTypes.includes('resident')) {
            options.push({
              id: 'resident',
              label: 'Therapist',
              price: 'Covered by your program',
              description: 'Best fit for concerns covered within this program'
            });
          }
          if (availableTherapistTypes.includes('associate')) {
            options.push({
              id: 'associate',
              label: 'Associate Therapist',
              price: 'Covered by your program',
              description: 'Experienced therapists - ideal when budget isn\'t your main concern'
            });
          }
          return options;
        }

        // Default options for regular quiz
        return [
          {
            id: 'resident',
            label: 'Therapist',
            price: `$${TIER_RATE_BOUNDS.resident.min}-$${TIER_RATE_BOUNDS.resident.max} per session`,
            description: 'Best fit for concerns covered within this program'
          },
          {
            id: 'associate',
            label: 'Associate Therapist',
            price: `$${TIER_RATE_BOUNDS.associate.min}-$${TIER_RATE_BOUNDS.associate.max} per session`,
            description: 'Experienced therapists - ideal when budget isn\'t your main concern'
          }
        ];
      })()
    },
    {
      id: 'rate',
      question: "Select your session rate",
      subtitle: "Your therapist's standard rate is shown below. When you pay the standard rate, you help make therapy accessible for those who can't.",
      type: 'rate-selection',
      sliderConfig: {
        resident: {
          min: TIER_RATE_BOUNDS.resident.min,
          max: TIER_RATE_BOUNDS.resident.max,
          default: TIER_RATE_BOUNDS.resident.max,
          standard: TIER_RATE_BOUNDS.resident.max,
        },
        associate: {
          min: TIER_RATE_BOUNDS.associate.min,
          max: TIER_RATE_BOUNDS.associate.max,
          default: TIER_RATE_BOUNDS.associate.max,
          standard: TIER_RATE_BOUNDS.associate.max,
        },
      }
    },
    {
      id: 'timezone',
      question: "What's your time zone?",
      subtitle: "For scheduling purposes",
      type: 'single-select',
      options: [
        { id: 'America/Vancouver', label: 'Pacific Time (PT)', description: 'British Columbia' },
        { id: 'America/Edmonton', label: 'Mountain Time (MT)', description: 'Alberta, Northwest Territories' },
        { id: 'America/Winnipeg', label: 'Central Time (CT)', description: 'Manitoba, Nunavut (Central)' },
        { id: 'America/Regina', label: 'Saskatchewan Time', description: 'Saskatchewan (No DST, UTC-6 year-round)' },
        { id: 'America/Toronto', label: 'Eastern Time (EST/EDT)', description: 'Ontario, Quebec, Nunavut (Eastern)' },
        { id: 'America/Halifax', label: 'Atlantic Time (AST/ADT)', description: 'New Brunswick, Nova Scotia, Prince Edward Island' },
        { id: 'America/St_Johns', label: 'Newfoundland Time (NST/NDT)', description: 'Newfoundland and Labrador' }
      ]
    },
    {
      id: 'province',
      question: "Which province do you live in?",
      subtitle: "Therapists are licensed by province — we use this to make sure your matches can see clients where you live.",
      type: 'single-select',
      options: [
        { id: 'ON', label: 'Ontario' },
        { id: 'BC', label: 'British Columbia' },
        { id: 'AB', label: 'Alberta' },
        { id: 'QC', label: 'Quebec' },
        { id: 'MB', label: 'Manitoba' },
        { id: 'SK', label: 'Saskatchewan' },
        { id: 'NS', label: 'Nova Scotia' },
        { id: 'NB', label: 'New Brunswick' },
        { id: 'NL', label: 'Newfoundland and Labrador' },
        { id: 'PE', label: 'Prince Edward Island' },
        { id: 'YT', label: 'Yukon' },
        { id: 'NT', label: 'Northwest Territories' },
        { id: 'NU', label: 'Nunavut' }
      ]
    },
    {
      id: 'availability',
      question: "What times typically work best for you?",
      subtitle: "We'll match you with therapists available during these times",
      type: 'multi-select',
      options: [
        { id: 'weekday-morning', label: 'Weekday mornings', description: 'Mon-Fri, 7am - 12pm' },
        { id: 'weekday-afternoon', label: 'Weekday afternoons', description: 'Mon-Fri, 12pm - 5pm' },
        { id: 'weekday-evening', label: 'Weekday evenings', description: 'Mon-Fri, 5pm - 9pm' },
        { id: 'weekend-morning', label: 'Weekend mornings', description: 'Sat-Sun, 8am - 12pm' },
        { id: 'weekend-afternoon', label: 'Weekend afternoons', description: 'Sat-Sun, 12pm - 5pm' },
        { id: 'flexible', label: 'I have a flexible schedule', description: 'Can adapt to therapist availability' }
      ]
    }
  ];

  // Filter steps based on partnership mode
  const steps = allSteps.filter(step => {
    if (hideInsurance && step.id === 'insurance') return false;
    if (hideRate && step.id === 'rate') return false;
    if (!showProvince && step.id === 'province') return false;
    return true;
  });

  const currentStepData = steps[currentStep];
  if (!currentStepData) return null;
  const progress = ((currentStep + 1) / steps.length) * 100;

  const handleMultiSelect = (field: string, optionId: string) => {
    const currentValues = answers[field as keyof QuizAnswers] as string[] || [];
    
    // Special handling for availability
    if (field === 'availability') {
      if (optionId === 'flexible') {
        // If selecting flexible, clear all other options
        setAnswers({ ...answers, [field]: ['flexible'] });
        return;
      } else if (currentValues.includes('flexible')) {
        // If flexible is already selected and user selects another option, replace flexible
        setAnswers({ ...answers, [field]: [optionId] });
        return;
      }
    }
    
    const newValues = currentValues.includes(optionId)
      ? currentValues.filter(id => id !== optionId)
      : [...currentValues, optionId];
    setAnswers({ ...answers, [field]: newValues });
  };

  const handleSingleSelect = (field: string, value: any) => {
    let updatedAnswers = { ...answers, [field]: value };
    
    // When therapist type is selected, initialize rate to maximum
    if (field === 'therapistType') {
      const bounds = value === 'resident' ? TIER_RATE_BOUNDS.resident : TIER_RATE_BOUNDS.associate;
      updatedAnswers = { ...updatedAnswers, rate: bounds.max };
    }
    
    setAnswers(updatedAnswers);
    
    // Don't auto-advance for rate slider or if it's age verification with "no"
    if (field === 'rate') {
      return;
    }
    
    if (field === 'ageVerification' && value === 'no') {
      // Don't auto-advance if they're under 17
      return;
    }
    
    // Auto-advance after a brief delay to show selection
    setTimeout(() => {
      // Handle therapist type selection - show resident disclosure if needed
      if (field === 'therapistType' && value === 'resident' && !showResidentDisclosure) {
        setShowResidentDisclosure(true);
        return;
      }

      // Otherwise advance to next step
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
        // Scroll to top when auto-advancing to next step
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }, 300);
  };


  const handleNext = () => {
    // Check if user is under 18
    if (currentStepData.id === 'age-verification' && answers.ageVerification === 'no') {
      // Show message and don't proceed
      return;
    }

    // Removed the RSW-only + Resident combination check - users can now proceed with resident therapist even with RSW-only

    // Show resident disclosure after selecting resident therapist
    if (currentStepData.id === 'therapist-type' && answers.therapistType === 'resident' && !showResidentDisclosure) {
      setShowResidentDisclosure(true);
      return;
    }

    // If on rate step and no rate selected, use the standard (max) rate
    if (currentStepData.id === 'rate' && !answers.rate) {
      const therapistType = answers.therapistType || 'resident';
      const sliderConfig = currentStepData.sliderConfig?.[therapistType as 'resident' | 'associate'];
      if (sliderConfig) {
        setAnswers({ ...answers, rate: sliderConfig.max });
      }
    }

    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      // Scroll to top when moving to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      if (onComplete) {
        onComplete(answers as QuizAnswers);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Scroll to top when going back to previous step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const canProceed = () => {
    switch (currentStepData.id) {
      case 'age-verification':
        return !!answers.ageVerification;
      case 'session-type':
        return !!answers.sessionType;
      case 'needs':
        return true; // Always allow proceeding since it's optional
      case 'insurance':
        return (answers.insurance?.length || 0) > 0;
      case 'therapist-type':
        return !!answers.therapistType;
      case 'rate':
        return true; // Allow proceeding - will use standard rate if not selected
      case 'timezone':
        return !!answers.timezone;
      case 'province':
        return !!answers.province;
      case 'availability':
        return (answers.availability?.length || 0) > 0;
      default:
        return false;
    }
  };

  const therapistType = answers.therapistType || 'resident';
  const sliderConfig = currentStepData.sliderConfig?.[therapistType as 'resident' | 'associate'];

  // Reset slider view when reaching rate step
  useEffect(() => {
    if (currentStepData.id === 'rate') {
      setShowRateSlider(false);
      // Don't set a default rate - let user choose
    }
  }, [currentStep, currentStepData.id]);

  // Scroll to top whenever step changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-medium text-gray-900">Matching Quiz</h1>
            <span className="text-sm text-gray-500">{currentStep + 1} of {steps.length}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1">
            <div 
              className="bg-primary-600 h-1 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8 md:p-12">
          {/* Question */}
          <div className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-2">
              {currentStepData.question}
            </h2>
            <p className="text-gray-600">
              {currentStepData.subtitle}
            </p>
          </div>

          {/* Age verification */}
          {currentStepData.type === 'single-select' && currentStepData.id === 'age-verification' && (
            <div>
              <div className="space-y-4">
                {currentStepData.options?.map((option: any) => (
                  <button
                    key={option.id}
                    onClick={() => handleSingleSelect('ageVerification', option.id)}
                    className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                      answers.ageVerification === option.id
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg text-gray-900">{option.label}</h3>
                        <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                      </div>
                      {answers.ageVerification === option.id && (
                        <svg className="w-6 h-6 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
              
              {answers.ageVerification === 'no' && (
                <div className="mt-6 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold">We're sorry.</span> Our services are currently only available for individuals 17 years and older. 
                    If you're in crisis, please contact Kids Help Phone at 1-800-668-6868 or text CONNECT to 686868.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Session type */}
          {currentStepData.type === 'single-select' && currentStepData.id === 'session-type' && (
            <div className="grid md:grid-cols-2 gap-4">
              {currentStepData.options?.map((option: any) => (
                <button
                  key={option.id}
                  onClick={() => handleSingleSelect('sessionType', option.id)}
                  className={`p-6 rounded-lg border-2 transition-all text-center ${
                    answers.sessionType === option.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300 bg-white'
                  }`}
                >
                  <h3 className="font-semibold text-lg text-gray-900 mb-1">{option.label}</h3>
                  <p className="text-sm text-gray-600">{option.description}</p>
                </button>
              ))}
            </div>
          )}

          {/* Needs multi-select */}
          {currentStepData.type === 'multi-select' && currentStepData.id === 'needs' && (
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {currentStepData.options?.map((option: any) => (
                  <button
                    key={option.id}
                    onClick={() => handleMultiSelect('needs', option.id)}
                    className={`p-4 rounded-lg border transition-all text-left ${
                      answers.needs?.includes(option.id)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <span className="font-medium text-gray-900">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Insurance coverage */}
          {currentStepData.type === 'multi-select' && currentStepData.id === 'insurance' && (
            <div className="space-y-4">
              {currentStepData.options?.map((option: any) => (
                <button
                  key={option.id}
                  onClick={() => {
                    // Handle mutual exclusivity with 'none'
                    if (option.id === 'none') {
                      setAnswers({ ...answers, insurance: ['none'] });
                    } else if (answers.insurance?.includes('none')) {
                      setAnswers({ ...answers, insurance: [option.id] });
                    } else {
                      handleMultiSelect('insurance', option.id);
                    }
                  }}
                  className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                    answers.insurance?.includes(option.id)
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-lg text-gray-900">{option.label}</h3>
                      <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                    </div>
                    {answers.insurance?.includes(option.id) && (
                      <svg className="w-6 h-6 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* Therapist type */}
          {currentStepData.type === 'single-select' && currentStepData.id === 'therapist-type' && (
            <div>
              <div className="mb-4 text-center">
                <button
                  onClick={() => setShowTherapistInfo(!showTherapistInfo)}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium inline-flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  What's the difference?
                </button>
              </div>

              {showTherapistInfo && (
                <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Therapists (Program Coverage)</h4>
                      <p className="text-sm text-gray-600">
                        This option is designed for concerns covered within your program, including anxiety, depression, relationships, and life transitions.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <strong>Insurance:</strong> RP (Registered Psychotherapist) coverage only
                      </p>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Associate Therapists</h4>
                      <p className="text-sm text-gray-600">
                        If budget isn't your main concern, these experienced therapists offer the confidence that comes with completed training and broader expertise. With years of experience, they've seen more situations similar to yours and can work with everything from daily stress to complex mental health needs, trauma, addiction, and court-mandated therapy.
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        <strong>Why choose Associate:</strong> More clinical hours, specialized training (EMDR, CBT, DBT), and broader insurance coverage (RP and RSW)
                      </p>
                    </div>
                    <button
                      onClick={() => setShowTherapistInfo(false)}
                      className="text-sm text-primary-600 hover:text-primary-700"
                    >
                      Got it
                    </button>
                  </div>
                </div>
              )}

              <div className="space-y-4">
                {currentStepData.options?.map((option: any) => {
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSingleSelect('therapistType', option.id)}
                      className={`w-full p-6 rounded-lg border-2 transition-all text-left ${
                        answers.therapistType === option.id
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg text-gray-900">{option.label}</h3>
                          <p className="text-primary-600 font-medium mt-1">{option.price}</p>
                          <p className="text-sm text-gray-600 mt-2">{option.description}</p>
                        </div>
                        {answers.therapistType === option.id && (
                          <svg className="w-6 h-6 text-primary-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {/* RSW-only warning */}
              {answers.insurance?.includes('rsw') && !answers.insurance?.includes('rp') && !answers.insurance?.includes('none') && !answers.insurance?.includes('other') && (
                <>
                  <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm text-amber-800">
                          <span className="font-semibold">Good to know:</span> This therapist option does not include RSW coverage. Choose an Associate therapist if your plan requires RSW.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Ontario residents only notice for RSW */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <div>
                        <p className="text-sm text-blue-800">
                          <span className="font-semibold">Ontario Residents Only:</span> Registered Social Workers (RSW) are licensed by province. Our RSW therapists can only see clients who reside in Ontario.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
              
              {/* Resident Disclosure */}
              {showResidentDisclosure && answers.therapistType === 'resident' && (
                <div className="mt-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                  <h3 className="font-semibold text-lg text-gray-900 mb-3">
                    Please Review: Program Coverage and Limits
                  </h3>
                  <p className="text-sm text-gray-700 mb-4">
                    This program option covers common therapy concerns, but certain specialized needs are not covered within this program option.
                  </p>
                  <ul className="space-y-2 mb-4">
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Crisis Support:</strong> Immediate crisis intervention or emergency support.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Psychiatric Conditions:</strong> Complex psychiatric disorders, such as schizophrenia or psychosis.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Dissociative Disorders:</strong> Treatment of dissociative disorders.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Legal Activity Support:</strong> Involved or may possibly become involved in any type of legal proceedings.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Recent Trauma:</strong> Trauma that has not been previously addressed or supported within the last year.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Untreated Personality Disorders:</strong> Personality disorders that have not recently undergone specialty treatment.</span>
                    </li>
                    <li className="text-sm text-gray-600 flex items-start gap-2">
                      <span className="text-gray-400 mt-1">•</span>
                      <span><strong>Substance Dependence:</strong> Situations involving daily reliance on substances.</span>
                    </li>
                  </ul>
                  <p className="text-sm text-gray-700 mb-4">
                    If any of these apply to your situation, please seek support from an Associate Therapist. This ensures that you receive the most appropriate and effective care.
                  </p>
                  <div className="flex gap-4">
                    <button
                      onClick={() => setCurrentStep(currentStep + 1)}
                      className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm"
                    >
                      Continue with this Therapist Option
                    </button>
                    <button
                      onClick={() => {
                        setAnswers({ ...answers, therapistType: 'associate' });
                        setShowResidentDisclosure(false);
                      }}
                      className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"
                    >
                      Continue with an Associate Therapist
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Rate selection - two step process */}
          {currentStepData.type === 'rate-selection' && sliderConfig && (
            <div className="px-4">
              {!showRateSlider ? (
                // Step 1: Show standard rate and ask if it works
                <div className="space-y-6">
                  {/* Standard rate display */}
                  <div className="bg-gradient-to-br from-primary-50 to-white rounded-2xl p-6 border border-primary-100 text-center">
                    <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Standard Rate</p>
                    <div className="flex items-baseline justify-center gap-2 mb-1">
                      <span className="text-5xl font-bold text-gray-900">
                        ${sliderConfig.standard}
                      </span>
                    </div>
                    <p className="text-gray-600 text-sm">per session</p>
                  </div>

                  {/* Choice buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={() => {
                        handleSingleSelect('rate', sliderConfig.standard);
                        setShowRateSlider(false);
                        // Automatically move to next step after selecting standard rate
                        setTimeout(() => {
                          setCurrentStep(currentStep + 1);
                        }, 100);
                      }}
                      className="w-full p-4 rounded-lg border-2 border-gray-300 bg-white text-left hover:border-gray-400 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">I can pay the standard rate</div>
                          <div className="text-sm text-gray-600">${sliderConfig.standard} per session</div>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>

                    <button
                      onClick={() => {
                        // Initialize rate to max when showing slider
                        if (!answers.rate) {
                          handleSingleSelect('rate', sliderConfig.max);
                        }
                        setShowRateSlider(true);
                      }}
                      className="w-full p-4 rounded-lg border-2 border-gray-300 bg-white text-left hover:border-gray-400 transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-gray-900">I need sliding scale pricing</div>
                          <div className="text-sm text-gray-600">Choose from ${sliderConfig.min}-${sliderConfig.max}</div>
                        </div>
                        <svg className="w-5 h-5 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>

                  <p className="text-xs text-center text-gray-500">
                    🔒 Your therapist never knows what you pay
                  </p>
                </div>
              ) : (
                // Step 2: Show slider if they need a lower rate
                <div>
                  <div className="mb-6 text-center">
                    <button 
                      onClick={() => setShowRateSlider(false)}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium inline-flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Back to standard rate
                    </button>
                  </div>

                  {/* Slider container */}
                  <div className="relative pt-12 pb-8">
                    {/* Floating value display */}
                    <div 
                      className="absolute -top-2 transition-all duration-200 ease-out"
                      style={{
                        left: `${((answers.rate || sliderConfig.max) - sliderConfig.min) / (sliderConfig.max - sliderConfig.min) * 100}%`,
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="relative">
                        <div className="bg-gradient-to-br from-blue-500 to-purple-600 text-white px-4 py-2 rounded-xl font-bold text-lg shadow-lg">
                          ${answers.rate || sliderConfig.max}
                        </div>
                        <div className="absolute left-1/2 -translate-x-1/2 -bottom-2">
                          <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-t-[8px] border-t-purple-600"></div>
                        </div>
                      </div>
                    </div>

                    {/* Slider container */}
                    <div className="relative mt-8">
                      {/* Background track */}
                      <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-4 bg-gradient-to-r from-gray-100 to-gray-200 rounded-full shadow-inner"></div>
                      
                      {/* Progress track */}
                      <div 
                        className="absolute left-0 top-1/2 -translate-y-1/2 h-4 bg-gradient-to-r from-blue-400 via-blue-500 to-purple-600 rounded-full shadow-sm transition-all duration-200"
                        style={{
                          width: `${((answers.rate || sliderConfig.max) - sliderConfig.min) / (sliderConfig.max - sliderConfig.min) * 100}%`
                        }}
                      >
                        {/* Subtle shimmer effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-20 rounded-full"></div>
                      </div>

                      {/* Actual range input */}
                      <input
                        type="range"
                        min={sliderConfig.min}
                        max={sliderConfig.max}
                        value={answers.rate || sliderConfig.max}
                        onChange={(e) => handleSingleSelect('rate', parseInt(e.target.value))}
                        className="relative z-10 w-full h-4 bg-transparent appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-10 [&::-webkit-slider-thumb]:h-10 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-gradient-to-br [&::-webkit-slider-thumb]:from-white [&::-webkit-slider-thumb]:to-gray-50 [&::-webkit-slider-thumb]:shadow-[0_0_0_3px_rgba(59,130,246,0.2),0_6px_16px_rgba(0,0,0,0.15)] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:cursor-grab [&::-webkit-slider-thumb]:transition-all [&::-webkit-slider-thumb]:duration-200 [&::-webkit-slider-thumb:hover]:scale-110 [&::-webkit-slider-thumb:hover]:shadow-[0_0_0_5px_rgba(59,130,246,0.3),0_8px_20px_rgba(0,0,0,0.2)] [&::-webkit-slider-thumb:active]:cursor-grabbing [&::-webkit-slider-thumb:active]:scale-105 [&::-moz-range-thumb]:w-10 [&::-moz-range-thumb]:h-10 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-gradient-to-br [&::-moz-range-thumb]:from-white [&::-moz-range-thumb]:to-gray-50 [&::-moz-range-thumb]:shadow-[0_0_0_3px_rgba(59,130,246,0.2),0_6px_16px_rgba(0,0,0,0.15)] [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:cursor-grab [&::-moz-range-thumb]:transition-all [&::-moz-range-thumb]:duration-200 [&::-moz-range-thumb:hover]:scale-110 [&::-moz-range-thumb:hover]:shadow-[0_0_0_5px_rgba(59,130,246,0.3),0_8px_20px_rgba(0,0,0,0.2)] [&::-moz-range-thumb:active]:cursor-grabbing [&::-moz-range-thumb:active]:scale-105"
                      />
                    </div>

                    {/* Slider labels */}
                    <div className="flex justify-between mt-8">
                      <div>
                        <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Lowest rate</div>
                        <div className="text-lg font-bold text-gray-800">${sliderConfig.min}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-400 font-medium uppercase tracking-wider">Standard rate</div>
                        <div className="text-lg font-bold text-gray-800">${sliderConfig.max}</div>
                      </div>
                    </div>
                  </div>

                  {/* Selected rate display */}
                  <div className="mt-8 text-center">
                    <p className="text-sm text-gray-500 mb-1">Your rate</p>
                    <div className="flex items-baseline justify-center gap-2">
                      <span className="text-3xl font-bold text-gray-900">
                        ${answers.rate || sliderConfig.min}
                      </span>
                      <span className="text-gray-600">per session</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timezone */}
          {currentStepData.type === 'single-select' && currentStepData.id === 'timezone' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {currentStepData.options?.map((option: any) => (
                <button
                  key={option.id}
                  onClick={() => handleSingleSelect('timezone', option.id)}
                  className={`p-4 rounded-lg border transition-all text-left ${
                    answers.timezone === option.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-600">{option.description}</div>
                </button>
              ))}
            </div>
          )}

          {/* Province */}
          {currentStepData.type === 'single-select' && currentStepData.id === 'province' && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {currentStepData.options?.map((option: any) => (
                <button
                  key={option.id}
                  onClick={() => handleSingleSelect('province', option.id)}
                  className={`p-4 rounded-lg border transition-all text-left ${
                    answers.province === option.id
                      ? 'border-primary-600 bg-primary-50'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <div className="font-medium text-gray-900">{option.label}</div>
                </button>
              ))}
            </div>
          )}

          {/* Availability */}
          {currentStepData.type === 'multi-select' && currentStepData.id === 'availability' && (
            <div className="space-y-6">
              {/* Weekdays */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Weekdays</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {currentStepData.options?.filter((opt: any) => opt.id.startsWith('weekday')).map((option: any) => (
                    <button
                      key={option.id}
                      onClick={() => handleMultiSelect('availability', option.id)}
                      className={`p-4 rounded-lg border transition-all ${
                        answers.availability?.includes(option.id)
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{option.label.replace('Weekday ', '')}</div>
                      <div className="text-sm text-gray-600">{option.description.replace('Mon-Fri, ', '')}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Weekends */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-3">Weekends</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {currentStepData.options?.filter((opt: any) => opt.id.startsWith('weekend')).map((option: any) => (
                    <button
                      key={option.id}
                      onClick={() => handleMultiSelect('availability', option.id)}
                      className={`p-4 rounded-lg border transition-all ${
                        answers.availability?.includes(option.id)
                          ? 'border-primary-600 bg-primary-50'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{option.label.replace('Weekend ', '')}</div>
                      <div className="text-sm text-gray-600">{option.description.replace('Sat-Sun, ', '')}</div>
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Flexible option */}
              <div className="mt-4">
                <div className="relative">
                  <div className="absolute inset-0 flex items-center" aria-hidden="true">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center">
                    <span className="px-3 bg-white text-sm text-gray-500">or</span>
                  </div>
                </div>
                
                {currentStepData.options?.filter((opt: any) => opt.id === 'flexible').map((option: any) => (
                  <button
                    key={option.id}
                    onClick={() => handleMultiSelect('availability', option.id)}
                    className={`mt-4 w-full p-5 rounded-lg border-2 transition-all ${
                      answers.availability?.includes(option.id)
                        ? 'border-primary-600 bg-primary-50'
                        : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          answers.availability?.includes(option.id)
                            ? 'bg-primary-100'
                            : 'bg-gray-100'
                        }`}>
                          <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.description}</div>
                        </div>
                      </div>
                      {answers.availability?.includes(option.id) && (
                        <svg className="w-6 h-6 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}



          {/* Navigation */}
          <div className="flex items-center justify-between mt-12">
            {currentStep > 0 ? (
              <button
                onClick={handleBack}
                className="text-gray-500 hover:text-gray-700 text-sm flex items-center gap-1 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Previous</span>
              </button>
            ) : (
              <div></div>
            )}
            <Button
              onClick={handleNext}
              disabled={!canProceed() || (currentStep === steps.length - 1 && isSubmitting)}
              isLoading={currentStep === steps.length - 1 && isSubmitting}
              className="bg-primary-600 hover:bg-primary-700 px-8 py-2.5"
            >
              {currentStep === steps.length - 1 ? (isSubmitting ? 'Processing...' : 'Continue') : 'Continue'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}